import React from 'react';
import { Box, Grid, sxToStyle, Typography } from '../../../components/ui/LayoutPrimitives';
import { 
  BookOpen as LibraryBooks,
  CreditCard as Payment,
  GraduationCap as School,
  Trash2 as Delete
} from 'lucide-react';

import { InstitutionTemplate } from '../../../domain/Domain';
import { PAYMENT } from '../../s3-assets/Payment';

const Button = ({ children, className = '', fullWidth, href, size, startIcon, style, sx, type = 'button', variant, ...props }) => {
  const Component = href ? 'a' : 'button';
  const componentProps = href ? { href } : { type };

  return (
    <Component
      className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${fullWidth ? 'w-full' : ''} ${size === 'large' ? 'min-h-12 text-base' : ''} ${variant === 'contained' ? 'bg-slate-900 text-white' : 'bg-transparent'} ${variant === 'outlined' ? 'border border-current' : ''} ${className}`}
      style={{ ...sxToStyle(sx), ...(style || {}) }}
      {...componentProps}
      {...props}
    >
      {startIcon}
      {children}
    </Component>
  );
};

const Avatar = ({ children, className = '', style, sx }) => (
  <div className={`inline-flex shrink-0 items-center justify-center rounded-full ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }}>
    {children}
  </div>
);

const Card = ({ children, className = '', style, sx }) => (
  <div className={`bg-white ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }}>
    {children}
  </div>
);

const CardContent = ({ children, className = '' }) => <div className={`p-4 ${className}`}>{children}</div>;

const CardActions = ({ children, className = '', style, sx }) => (
  <div className={`flex items-center gap-2 p-4 ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }}>
    {children}
  </div>
);

const Chip = ({ className = '', label, style, sx }) => (
  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }}>
    {label}
  </span>
);

const CircularProgress = ({ size = 40, style, sx }) => (
  <span
    className="inline-block animate-spin rounded-full border-4 border-current border-r-transparent"
    style={{ width: size, height: size, ...sxToStyle(sx), ...(style || {}) }}
  />
);

const Divider = ({ className = '', style, sx }) => (
  <hr className={`border-0 border-t border-slate-200 ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }} />
);

const Paper = ({ children, className = '', elevation, style, sx }) => (
  <div className={`rounded-lg bg-white ${className}`} style={{ ...sxToStyle(sx), ...(style || {}) }}>
    {children}
  </div>
);
const InstitutionCart = ({ 
  items, 
  totalAmount, 
  currencySymbol, 
  onRemove, 
  onCheckout,
  onContinueShopping,
  userDetail,
  isLoading
}) => {
  return (
    <InstitutionTemplate>
        <br /> <br />
        <br />
      <Box sx={{ 
        py: 11, 
        px: { xs: 2, md: 4 },
        maxWidth: 1400,
        margin: '0 auto',
      }}>
        {/* Header Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              mb: 3
            }}>
              <Avatar sx={{ 
                bgcolor: '#B5A262',
                mr: 2,
                width: 56,
                height: 56,
              }}>
                <School className="h-6 w-6" aria-hidden="true" />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 600,
                  color: "#333",
                  mb: 0.5,
                }}>
                  {userDetail?.firstName ? `${userDetail.firstName}'s Cart` : "Your Cart"}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Review your selected programs before submission
                </Typography>
              </Box>
            </Box>

            {isLoading ? (
              <Box sx={{ 
                display: "flex", 
                flexDirection: 'column',
                justifyContent: "center", 
                alignItems: 'center',
                py: 8,
                minHeight: '200px'
              }}>
                <CircularProgress size={50} thickness={4} sx={{ color: '#B5A262', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#666' }}>
                  Loading your applications...
                </Typography>
              </Box>
            ) : items.length > 0 ? (
              <>
                <Grid container spacing={2}>
                  {items.map((item, index) => (
                    <Grid item xs={12} key={index}>
                      <Card sx={{
                        borderRadius: 1,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }
                      }}>
                      <CardContent>
  <Grid container spacing={2}>
    {/* LEFT: Image Section */}
    <Grid item xs={12} md={4}>
      <Box
        component="img"
        src={PAYMENT.IMAGES.IMAGE_2}
        alt="Institution image"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: 2,
        }}
      />
    </Grid>

    {/* RIGHT: Text Content */}
    <Grid item xs={12} md={8}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#3B3F5C' }}>
          {item.scheduledProduct?.product?.name}
        </Typography>

        {item.scheduledProduct?.product?.description && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: '#666',
              lineHeight: 1.6,
            }}
          >
            {item.scheduledProduct.product.description}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        {item.scheduledProduct?.product?.code && (
          <Chip
            label={`Program Code: ${item.scheduledProduct.product.primaryId}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(181, 162, 98, 0.1)',
              color: '#B5A262',
              border: '1px solid rgba(181, 162, 98, 0.3)',
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', md: 'flex-end' },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}
        >
          {currencySymbol}
          {(item.scheduledProduct?.product?.price?.priceAmt).toFixed(2)}
        </Typography>

        <CardActions
          sx={{
            p: 0,
            mt: 2,
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<Delete />}
            onClick={() => onRemove(item.scheduledProduct?.primaryId)}
            sx={{
              color: '#d32f2f',
              borderColor: '#d32f2f',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            Remove
          </Button>
        </CardActions>
      </Box>
    </Grid>
  </Grid>
</CardContent>

                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              <Box sx={{ 
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: '50vh',
                textAlign: "center",
                p: 3,
              }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(181, 162, 98, 0.1)',
                  width: 80,
                  height: 80,
                  mb: 3,
                  color: '#B5A262'
                }}>
                  <LibraryBooks className="h-10 w-10" aria-hidden="true" />
                </Avatar>
                
                <Typography variant="h4" sx={{ 
                  mb: 1.5,
                  color: '#333',
                  fontWeight: 600
                }}>
                  No Programs Selected
                </Typography>
                
                <Typography variant="body1" sx={{ 
                  mb: 3,
                  maxWidth: '500px',
                  color: '#666',
                }}>
                  You haven't added any academic programs to your application yet. Explore our catalog to begin your educational journey.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={onContinueShopping}
                    sx={{ 
                      color: "#B5A262",
                      borderColor: '#B5A262',
                      px: 4,
                      '&:hover': {
                        backgroundColor: 'rgba(181, 162, 98, 0.1)',
                        borderColor: '#B5A262'
                      }
                    }}
                  >
                    Browse Programs
                  </Button>
                  
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={onContinueShopping}
                    sx={{ 
                      px: 4,
                      backgroundColor: '#B5A262',
                      '&:hover': {
                        backgroundColor: '#A29258',
                      }
                    }}
                  >
                    View Catalog
                  </Button>
                </Box>
              </Box>
            )}
          </Grid>

          {items.length > 0 && (
            <Grid item xs={12} md={4}>
              <Box sx={{ position: 'sticky', top: 20 }}>
                {/* Summary Section */}
                <Paper elevation={0} sx={{ 
                  p: 2, 
                  mb: 2,
                  borderRadius: 1,
                  border: '1px solid #eee',
                }}>
                  <Typography variant="h5" sx={{ 
                    mb: 2, 
                    display: 'flex',
                    alignItems: 'center',
                    color: '#333',
                    fontWeight: 600
                  }}>
                    <Payment className="mr-3 h-5 w-5" style={{ color: '#B5A262' }} aria-hidden="true" />
                     Summary
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {items.map((item, index) => (
                      <Box key={index} sx={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        mb: 1,
                        py: 1,
                        borderBottom: '1px solid #f5f5f5'
                      }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {item.scheduledProduct?.product?.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {currencySymbol}{(item.scheduledProduct?.product?.price?.priceAmt).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    mb: 2,
                    p: 1.5,
                    backgroundColor: 'rgba(181, 162, 98, 0.1)',
                    borderRadius: 1
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                      Total Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                      {currencySymbol} {totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={onCheckout}
                    sx={{ 
                      py: 1,
                      fontWeight: 600,
                      backgroundColor: '#B5A262',
                      '&:hover': {
                        backgroundColor: '#A29258',
                      }
                    }}
                  >
                    Check out
                  </Button>
                </Paper>
                
                {/* Help Section */}
                <Paper elevation={0} sx={{ 
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #eee',
                }}>
                  <Typography variant="h6" sx={{ 
                    mb: 1.5,
                    color: '#333',
                    fontWeight: 600
                  }}>
                    Need Help With Your Application?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1.5, color: '#666' }}>
                    Our admissions team is available to assist you with any questions about programs or the application process.
                  </Typography>
                 <Button
                                            href="/enquiry"
                                            variant="text"
                                            size="small"
                                            sx={{
                                                color: '#B5A262',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                fontSize: '1rem',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                    backgroundColor: 'transparent',
                                                },
                                            }}
                                        >
                                            Enquiry Admissions →
                                        </Button>
                </Paper>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </InstitutionTemplate>
  );
};

export default InstitutionCart;
